import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import authService from "../services/auth";
import PasswordInput from "../components/PasswordInput";
import {
  checkPasswordRequirements,
  isPasswordValid as checkIsPasswordValid,
  PASSWORD_REQUIREMENT_LABELS,
} from "../config/passwordValidation";
import "./Login.css";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Use centralized password validation
  const passwordChecks = useMemo(
    () => checkPasswordRequirements(password),
    [password],
  );

  const isPasswordValid = checkIsPasswordValid(password);
  const passwordsMatch = password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Invalid reset link. Please request a new password reset.");
      return;
    }

    if (!passwordsMatch) {
      setError("Passwords do not match");
      return;
    }

    if (!isPasswordValid) {
      setError("Please meet all password requirements");
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(token, password);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // No token in URL
  if (!token) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>Invalid Link</h1>
          <div className="error-message">
            This password reset link is invalid or has expired.
          </div>
          <div className="login-footer">
            <p>
              <Link to="/forgot-password">Request a new reset link</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>Password Reset</h1>
          <div className="success-message">
            <p>Your password has been reset successfully!</p>
            <p>Redirecting to login...</p>
          </div>
          <div className="login-footer">
            <p>
              <Link to="/login">Go to Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>Kwibl</h1>
        <h2>Set New Password</h2>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
              required
              minLength={8}
            />
            {password && (
              <div className="password-requirements">
                {Object.entries(PASSWORD_REQUIREMENT_LABELS).map(
                  ([key, label]) => (
                    <small
                      key={key}
                      className={passwordChecks[key] ? "valid" : "invalid"}
                    >
                      {passwordChecks[key] ? "\u2713" : "\u2717"} {label}
                    </small>
                  ),
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <PasswordInput
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              autoComplete="new-password"
              required
              minLength={8}
            />
            {confirmPassword && !passwordsMatch && (
              <small className="invalid">
                {"\u2717"} Passwords do not match
              </small>
            )}
            {confirmPassword && passwordsMatch && (
              <small className="valid">{"\u2713"} Passwords match</small>
            )}
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !isPasswordValid || !passwordsMatch}
          >
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>

        <div className="login-footer">
          <p>
            <Link to="/login">Back to Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
